import { Box, Button, Divider, Flex, Text, VStack } from '@chakra-ui/react'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { useQueryClient } from 'react-query'
import { getSessionControllerGetProgressNotesForSessionQueryKey, useSessionControllerSelectVersion } from '~/clinician-api'
import type { SessionProgressNoteDto } from '~/clinician-api/models'

export type NoteHandle = {
  getScrollElement: () => HTMLDivElement | null
  getSectionElements: () => HTMLDivElement[]
}

type NoteProps = {
  isLeft: boolean
  sections: { title: string; content: string }[]
  onSelect: () => void
  isLoading: boolean
  isDisabled: boolean
}

export const Note = forwardRef<NoteHandle, NoteProps>(
  ({ isLeft, sections, onSelect, isLoading, isDisabled }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const sectionRefs = useRef<HTMLDivElement[]>([])

    useImperativeHandle(ref, () => ({
      getScrollElement: () => scrollRef.current,
      getSectionElements: () => sectionRefs.current
    }))

    return (
      <Flex
        flexDirection="column"
        border="1px solid #E4E5E6"
        bg="#F9F9F9"
        borderRadius="8px"
        flex="1"
        minWidth="0"
        position="relative"
      >
        <Box h="100%" overflow="hidden" flex="1">
          <Flex py="16px" gap="8px" px="16px">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 16H16V18H8V16ZM8 12H16V14H8V12ZM14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                fill="black"
              />
            </svg>
            <Text fontWeight="bold">SOAP</Text>
          </Flex>
          <Divider borderColor="#E4E5E6" px="16px" />
          <Box
            overflow="auto"
            h="100%"
            ref={scrollRef}
            pl="16px"
            pr="calc(16px + env(safe-area-inset-right))"
            pb="200px"
          >
            <VStack gap="16px" align="flex-start">
              {sections.map((section, i) => (
                <Box
                  key={section.title}
                  py="8px"
                  ref={el => {
                    if (el) {
                      sectionRefs.current[i] = el
                    }
                  }}
                >
                  <Text fontSize="14px" fontWeight="bold">
                    {section.title}
                  </Text>
                  <Text fontSize="14px">{section.content}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>
        <Box
          bg="#F9F9F9"
          py="16px"
          px="16px"
          position="absolute"
          bottom="0"
          left="0"
          right="0"
        >
          <Button
            isFullWidth
            borderRadius="8px"
            m={0}
            onClick={onSelect}
            isLoading={isLoading}
            isDisabled={isDisabled}
            leftIcon={isLeft ? <ArrowBackIcon fontSize="small" /> : undefined}
            rightIcon={
              isLeft ? undefined : <ArrowForwardIcon fontSize="small" />
            }
          >
            {isLeft ? 'Left is better' : 'Right is better'}
          </Button>
        </Box>
      </Flex>
    )
  }
)

const buildNoteSections = (note: SessionProgressNoteDto) => {
  return note.template.sections.map(section => ({
    title: section.displayName,
    content: note.note[section.key] as string
  }))
}

const ProgressNoteComparison = ({
  sessionId,
  firstNote,
  secondNote,
}: {
  sessionId: string
  firstNote: SessionProgressNoteDto
  secondNote: SessionProgressNoteDto
}) => {
  const leftRef = useRef<NoteHandle>(null)
  const rightRef = useRef<NoteHandle>(null)
  const [selectedNote, setSelectedNote] = useState<'left' | 'right' | null>(null)

  const queryClient = useQueryClient()

  const { mutate: selectComparisonNote, isLoading: isSelectingComparisonNote } =
    useSessionControllerSelectVersion()

  const equalizeHeights = () => {
    const leftSections = leftRef.current?.getSectionElements() ?? []
    const rightSections = rightRef.current?.getSectionElements() ?? []

    for (let i = 0; i < firstNote.template.sections.length; i++) {
      const leftEl = leftSections[i]
      const rightEl = rightSections[i]
      if (leftEl && rightEl) {
        // Reset heights to auto first
        leftEl.style.height = 'auto'
        rightEl.style.height = 'auto'

        // Force a reflow to get the new natural heights
        leftEl.offsetHeight
        rightEl.offsetHeight

        const maxHeight = Math.max(leftEl.scrollHeight, rightEl.scrollHeight)
        leftEl.style.height = `${maxHeight}px`
        rightEl.style.height = `${maxHeight}px`
      }
    }
  }

  useEffect(() => {
    equalizeHeights()

    const handleResize = () => {
      equalizeHeights()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Sync scroll positions
  useEffect(() => {
    const leftScroll = leftRef.current?.getScrollElement()
    const rightScroll = rightRef.current?.getScrollElement()

    if (!leftScroll || !rightScroll) return

    let isSyncing = false

    const sync = (source: HTMLElement, target: HTMLElement) => {
      if (isSyncing) return
      isSyncing = true
      target.scrollTop = source.scrollTop
      requestAnimationFrame(() => {
        isSyncing = false
      })
    }

    const onLeftScroll = () => sync(leftScroll, rightScroll)
    const onRightScroll = () => sync(rightScroll, leftScroll)

    leftScroll.addEventListener('scroll', onLeftScroll)
    rightScroll.addEventListener('scroll', onRightScroll)

    return () => {
      leftScroll.removeEventListener('scroll', onLeftScroll)
      rightScroll.removeEventListener('scroll', onRightScroll)
    }
  }, [])

  const handleSelectComparisonNote = (
    noteId: string,
    version: 'left' | 'right'
  ) => {
    setSelectedNote(version)
    selectComparisonNote(
      {
        id: sessionId,
        noteId: noteId,
        data: {
          versionSelected: version
        }
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(
            getSessionControllerGetProgressNotesForSessionQueryKey(sessionId)
          )
        }
      }
    )
  }

  return (
    <Flex
      flexDirection="column"
      h="100%"
      px="32px"
      py="16px"
      height="calc(100vh - 150px)"
    >
      <Box px="32px" py="8px" mb="16px">
        <Text fontSize="24px" fontWeight="bold" align="center">
          Which note do you prefer?
        </Text>
        <Text fontSize="16px" color="#757575" align="center">
          Your choice will help make notes better.
        </Text>
      </Box>

      <Flex
        flex="1"
        justifyContent="space-between"
        gap="16px"
        overflow="hidden"
      >
        <Note
          ref={leftRef}
          sections={buildNoteSections(firstNote)}
          isLeft
          onSelect={() => handleSelectComparisonNote(firstNote.id, 'left')}
          isLoading={isSelectingComparisonNote && selectedNote === 'left'}
          isDisabled={selectedNote === 'right'}
        />
        <Note
          ref={rightRef}
          sections={buildNoteSections(secondNote)}
          isLeft={false}
          onSelect={() => handleSelectComparisonNote(secondNote.id, 'right')}
          isLoading={isSelectingComparisonNote && selectedNote === 'right'}
          isDisabled={selectedNote === 'left'}
        />
      </Flex>
    </Flex>
  )
}

export default ProgressNoteComparison
